import { createSlice, createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
    fetchPortfoliosInBatches,
    fetchDetailedPortfolioObjects,
    updatePortfolioDetails,
    updateUserPortfolioData,
    saveUserPortfolioSnapshot,
    createNewPortfolio,
    deletePortfolios,
    fetchUserPortfolioHistory,
    updatePortfolioPositions
} from "../../api/portfolioApi";
import {deepUpdate} from "../../utils/tools";
import {STATUS_IDLE, STATUS_LOADING, STATUS_FAILED, STATUS_SUCCEEDED} from "../statusCodes";


const portfolioInitialState = {
    items: [], // The portfolios
    allNotRemoved: [],
    lastNotRemoved: [],
    signalHistory: {}, // Signal history for each portfolio
    status: STATUS_IDLE,
    error: null,
    updateFlag: false
}





/**
 *
 * @type {Slice<{
 *      lastNotRemoved: *[],
 *      allNotRemoved: *[],
 *      signalHistory: {},
 *      error: null,
 *      items: *[],
 *      status: string
 *      }, {}, string, string, SliceSelectors<{lastNotRemoved: *[], allNotRemoved: *[], signalHistory: {}, error: null, items: *[], status: string}>>}
 */

const portfolioSlice = createSlice({
  name: 'portfolios',
  initialState: portfolioInitialState,
  reducers: {
      resetUpdateFlag: (state) => {
        state.updateFlag = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfoliosInBatches.pending, (state, action) => {state.status = 'loading'})
      .addCase(fetchPortfoliosInBatches.fulfilled, (state, action) => {
          if (action.payload) {
              state.status = STATUS_SUCCEEDED
              action.payload.forEach(fetchedPortfolio => {
                  const index = state.items.findIndex(item => item.id === fetchedPortfolio.id);
                  if (index !== -1) {
                      deepUpdate(state.items[index], fetchedPortfolio);  // Deeply update the existing portfolio
                  } else {
                      state.items.push(fetchedPortfolio); // Add new portfolio to the items array
                  }
              });
          }

      })
      .addCase(fetchPortfoliosInBatches.rejected, (state, action) => {
        state.status = STATUS_FAILED;
        state.error = action.payload;
      })

      .addCase(updatePortfolioDetails.pending, (state) => {
          state.status = STATUS_LOADING;
      })
      .addCase(updatePortfolioDetails.fulfilled, (state, action) => {
              const { id, data } = action.payload;
              const portfolioIndex = state.items.findIndex(item => item.id === id);

              if (portfolioIndex !== -1) {
                // Create a new object by deeply merging the existing portfolio with the updated data
                const updatedPortfolio = _.merge({}, state.items[portfolioIndex], data);

                // Create a new array with the updated portfolio
                const updatedItems = [
                  ...state.items.slice(0, portfolioIndex),
                  updatedPortfolio,
                  ...state.items.slice(portfolioIndex + 1)
                ];
                state.items = updatedItems;
              }

      })
      .addCase(updatePortfolioDetails.rejected, (state, action) => {
            state.status = STATUS_FAILED;
            state.error = action.payload;
      })
      .addCase(updateUserPortfolioData.fulfilled, (state, action) => {
          const { portfolioID, updateFields } = action.meta.arg; // Extract portfolioID & updateFields from action arg
          const index = state.items.findIndex(item => item.id === portfolioID);  // Index of the portfolio to update

          if (index !== -1) {
              // Only update if necessary
              const currentUser = state.items[index]._user || {};
              if (!_.isEqual(currentUser, updateFields)) {
                state.items[index] = {
                  ...state.items[index],
                  _user: {
                    ...currentUser,
                    ...updateFields
                  }
                };
              }
          }
      })
      .addCase(saveUserPortfolioSnapshot.fulfilled, (state, action) => {
        const { portfolioID, timestamp } = action.payload;
        const index = state.items.findIndex(item => item.id === portfolioID);
        if (index !== -1) {
          if (!state.items[index]._user) {
            state.items[index]._user = {}; // Initialize _user if it doesn't exist
          }
          state.items[index]._user.last_ack = timestamp;
        }
      })
      .addCase(fetchDetailedPortfolioObjects.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDetailedPortfolioObjects.fulfilled, (state, action) => {
            state.status = 'succeeded';
            action.payload.forEach(detail => {
                if (detail && detail.id) {
                    const index = state.items.findIndex(item => item.id === detail.id);
                    if (index !== -1) {
                        // Deeply merge detail into the existing item, prioritizing new detail fields
                        _.merge(state.items[index], detail);
                        // Mark as detailed data fetched
                        state.items[index].detailsFetched = true;
                    } else {
                        // If no existing item matches, add the detailed object as a new item
                        // and mark it as detailed data fetched
                        state.items.push({...detail, detailsFetched: true});
                    }
                }
            });
      })
      .addCase(fetchDetailedPortfolioObjects.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(createNewPortfolio.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const { portfolio_object, user_portfolio_object } = action.payload;
            const newPortfolio = {
                ...portfolio_object,
                _user: user_portfolio_object // Nesting the user portfolio object
            };
            // Add the new portfolio to the items array
            state.items.push(newPortfolio);
      })
      .addCase(deletePortfolios.fulfilled, (state, action) => {
          const { removed, not_removed, removed_from_user } = action.payload;

          // Remove portfolios from 'items'
          state.items = state.items.filter(item => !removed.includes(item.id) && !removed_from_user.includes(item.id));

        // Update 'last_not_removed' with the latest 'not_removed' IDs or an empty array
        state.lastNotRemoved = not_removed || [];
        // Update 'all_not_removed' by adding new 'not_removed' IDs, ensuring no duplicates
        state.allNotRemoved = [...new Set([...state.allNotRemoved, ...not_removed])];
      })
      .addCase(fetchUserPortfolioHistory.fulfilled, (state, action) => {
          const { portfolioId, historyData } = action.payload;
          state.signalHistory[portfolioId] = historyData;
      })
      .addCase(updatePortfolioPositions.fulfilled, (state, action) => {
        const { portfolioId, ptfl, cash } = action.payload; // Extract portfolioId and ptfl from the action payload
        const portfolioIndex = state.items.findIndex(item => item.id === portfolioId); // Find the portfolio to update

        if (portfolioIndex !== -1) {
            // Update the ptfl object for the specific portfolio
            state.items[portfolioIndex] = {
                ...state.items[portfolioIndex],
                ptfl: ptfl,
                cash: cash
            };
        }
    })
    },
});



const selectPortfolioItems = state => state.portfolios.items;
const selectPortfolioIds = (state, ids) => Array.isArray(ids) ? ids : [];

// Memoized selector
export const selectFullPortfolios = createSelector(
  [selectPortfolioItems, selectPortfolioIds],
  (items, validIds) =>
    items.filter(portfolio => validIds.includes(portfolio.id))
);

export const selectPortfoliosWithSignals = (state, { inUse = true } = {}) => {
    if (!state.portfolios.items || !Array.isArray(state.portfolios.items)) return [];
  const items = [...state.portfolios.items];
  const filteredPortfolios = items.filter(portfolio => {
    const isInUse = portfolio._user?.in_use === true;
    if (inUse && !isInUse || !inUse && isInUse) {
      return false;
    }
    const hasUnacknowledgedUpdates = !portfolio._user.hasOwnProperty('last_ack') || portfolio._user?.last_update > portfolio._user?.last_ack;
    return hasUnacknowledgedUpdates;
  });
  filteredPortfolios.sort((a, b) => (b._user?.last_update || 0) - (a._user?.last_update || 0));
  return filteredPortfolios;
}


export const selectStarredPortfolios = (state) =>
    state.portfolios.items.filter(portfolio => portfolio._user?.in_use === true);


export const selectPortfolioById = (state, id) => {
    // handle null or undefined id
    if (!id) return null;

    return state.portfolios.items.find(portfolio => portfolio.id === id);
}


export const selectPortfoliosWithUserUpdates = createSelector(
  [state => state.portfolios.items],
  (items) => {
    return items.filter(item => item?._user?.hasOwnProperty?.('in_use'));
  }
);
// Expose the reducer
export default portfolioSlice.reducer;
export const { resetUpdateFlag } = portfolioSlice.actions;




